/**
 * ErrorToast Component
 * Custom error toast with copy, report, and dismiss functionality.
 * Used with sonner's toast.custom() for persistent, actionable error notifications.
 */

import React, { useCallback } from "react";
import { X, Copy, MessageSquare, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import type { ReportableError } from "@/types/error.types";
import { formatErrorForClipboard } from "@/types/error.types";
import { useSupportDialogStore } from "@/stores/useSupportDialogStore";

export interface ErrorToastProps {
  /** Toast ID for dismissal */
  toastId: string | number;
  /** Primary error message */
  message: string;
  /** Optional secondary description */
  description?: string;
  /** Full error data for copy/report functionality */
  error: ReportableError;
  /** Whether the toast can be dismissed */
  dismissible?: boolean;
}

export function ErrorToast({
  toastId,
  message,
  description,
  error,
  dismissible = true,
}: ErrorToastProps) {
  const [copied, setCopied] = React.useState(false);
  const openWithError = useSupportDialogStore((s) => s.openWithError);

  const handleCopy = useCallback(async () => {
    try {
      const text = formatErrorForClipboard(error);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard not available
    }
  }, [error]);

  const handleReport = useCallback(() => {
    toast.dismiss(toastId);
    openWithError(error);
  }, [toastId, error, openWithError]);

  const handleDismiss = useCallback(() => {
    toast.dismiss(toastId);
  }, [toastId]);

  const truncatedCorrelationId = error.correlationId
    ? `${error.correlationId.slice(0, 8)}…`
    : undefined;

  return (
    <div
      className={cn(
        "w-[356px] rounded-lg shadow-lg",
        "bg-[#1a1a1a] border border-red-800/50",
        "text-white p-4",
      )}
      // Prevent clicks from propagating through to modal overlays behind
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <AlertCircle className="h-5 w-5 text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white select-text">
            {message}
          </p>
          {description && (
            <p className="mt-1 text-sm text-gray-400 select-text">
              {description}
            </p>
          )}
          {truncatedCorrelationId && (
            <p className="mt-1 text-xs text-gray-500 font-mono select-text inline-flex items-center gap-1">
              Ref: {truncatedCorrelationId}
              <CopyButton
                value={error.correlationId!}
                size="sm"
                className="inline-flex h-4 w-4 p-0 text-gray-500 hover:text-gray-300"
              />
            </p>
          )}
        </div>
        {dismissible && (
          <Button
            variant="utility"
            size="icon"
            onClick={handleDismiss}
            className="shrink-0"
            aria-label="Dismiss"
          >
            <X className="!h-[18px] !w-[18px]" />
          </Button>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 flex items-center gap-2">
        <Button
          variant="blue"
          size="sm"
          onClick={handleCopy}
          className={cn(
            copied && "!bg-green-500/15 !border-green-500/40 !text-green-400",
          )}
        >
          {copied ? (
            <>
              <Check className="!h-3.5 !w-3.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="!h-3.5 !w-3.5" />
              Copy Error
            </>
          )}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleReport}
          className="bg-[rgba(239,68,68,0.15)] border border-[rgba(239,68,68,0.4)] text-[#ef4444] hover:bg-[rgba(239,68,68,0.25)] hover:border-[rgba(239,68,68,0.6)] hover:text-[#f87171]"
        >
          <MessageSquare className="!h-3.5 !w-3.5" />
          Report Error
        </Button>
      </div>
    </div>
  );
}
